<template>
  <div v-if="featureStore.featureOrderManagment">
    <HeaderNavbar :title="pageTitle" :subtitle="pageSubtitle" :breadcrumbs="listBreadcrumbs">
      <template #right-side>
        <HeaderNavbarSearch
          v-model="searchQuery"
          v-model:sort="sortState"
          :sort-options="sortOptions"
          :placeholder="$t('orders.search_placeholder')" />
        <LeadSegmentedControl v-model="listLayout" :options="layoutOptions" />
        <LeadButton
          v-if="featureStore.featureCanCreateEditOrder"
          :as="RouterLink"
          to="/dashboard/orders/create"
          variant="primary"
          :icon="IconPlus"
          data-testid="orders-list-new-order-button">
          {{ $t('orders.create.btn') }}
        </LeadButton>
      </template>
      <template #tabs>
        <div class="tw:flex tw:justify-between tw:flex-wrap tw:gap-3">
          <div class="tw:flex tw:flex-wrap tw:gap-3">
            <LeadTabs
              class="tw:pt-3"
              :model-value="activeTab"
              :tabs="orderTabs"
              :rule="false"
              @update:model-value="setActiveTab">
              <template #trailing="{ tab, active }">
                <IconLoader2
                  v-if="active && isListLoading"
                  :size="14"
                  :stroke-width="2"
                  class="tw:shrink-0 tw:animate-spin tw:text-gray-400" />
                <span
                  v-else-if="tab.count != null"
                  class="tw:inline-flex tw:min-w-[18px] tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-blue-200 tw:bg-blue-50 tw:px-[5px] tw:py-0 tw:text-12 tw:font-medium tw:leading-4 tw:text-blue-500">
                  {{ tab.count }}
                </span>
              </template>
            </LeadTabs>
            <ToggleButton class="tw:self-center" :active="!!filters.creator_string" @click="toggleMyOrders">
              <IconUser :size="16" :stroke-width="2" class="tw:shrink-0" />
              <span>{{ $t('orders.list.filter_my') }}</span>
            </ToggleButton>
          </div>
          <FilterButton
            class="tw:self-center"
            :model-value="filters"
            :active-filter-count="popoverFilterCount"
            @submitFilters="applyDraft"
            @resetFilters="resetFilters" />
        </div>
      </template>
    </HeaderNavbar>

    <CustomerCards />

    <div class="container-lead">
      <div class="row row-cards">
        <div class="col-12">
          <div v-if="listLayout === 'card' && !isActiveTabEmpty" class="tw:mb-2.5 tw:flex tw:justify-end">
            <SortButton v-model="sortState" :sort-options="sortOptions" />
          </div>
          <RemoteListView
            v-if="tabReady"
            ref="remoteListRef"
            endpoint="/v1/account/orders"
            :search-query="searchQuery"
            :order="sortState.key"
            :order-dir="sortState.direction"
            :page-size="10"
            :show-footer="listLayout === 'card' && !(remoteListRef?.isLoading ?? true)"
            :reset-page="data.resetPage"
            :parameters="requestParameters"
            @page-resetted="data.resetPage = false"
            autopilot-key="order_list"
            :autopilot-description="orderListDescription"
            :autopilot-transformer="orderListTransformer"
            :autopilot-link="orderListLink"
            :autopilot-filters="orderListFilters"
            @after-fetch="onAfterFetch"
            @after-fetch-meta="onAfterFetchMeta">
            <template #empty>
              <ActiveOrdersEmptyView
                v-if="showActiveEmptyView"
                :has-active-filters="hasActiveFilters"
                @go-to-all="setActiveTab('all')"
                @reset-filters="clearAndRefetch" />
              <div v-else class="card card-body empty my-3" data-testid="orders-list-empty-default">
                <div class="empty-icon">
                  <IconZoomExclamation :size="24" :stroke-width="1.2" />
                </div>
                <p class="empty-title">{{ $t('orders.list.empty') }}</p>
                <p v-if="hasActiveFilters" class="empty-subtitle text-muted">
                  {{ $t('orders.list.empty.hint') }}
                </p>
                <div v-if="hasActiveFilters" class="empty-action">
                  <LeadButton
                    variant="primary"
                    :icon="IconX"
                    data-testid="orders-list-empty-default-reset-filters"
                    @click="clearAndRefetch">
                    {{ $t('orders.filters.reset_filters') }}
                  </LeadButton>
                </div>
              </div>
            </template>
            <template #loading>
              <!-- table view: LeadTable renders its own skeleton rows when :loading=true -->
              <LeadTable
                v-if="listLayout === 'table'"
                :columns="orderTableColumns"
                :rows="[]"
                :loading="true"
                :loading-label="$t('orders.list.loading')"
                :sort-key="sortState.key"
                :sort-dir="sortState.direction"
                :page-size="10"
                :item-label="paginationItemLabel"
                :show-footer="false" />
              <div v-else class="tw:flex tw:flex-col tw:gap-2.5">
                <!-- 5 skeleton cards mirroring OrderGridElement's structure -->
                <div
                  v-for="(sk, i) in gridSkeletonRows"
                  :key="`grid-loading-${i}`"
                  class="tw:flex tw:items-stretch tw:gap-5 tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-white tw:px-4 tw:shadow-xs">
                  <!-- initials tile placeholder -->
                  <LeadSkeleton variant="block" :width="44" :height="44" class="tw:mt-4 tw:shrink-0 tw:self-start" />
                  <!-- title block placeholder -->
                  <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2 tw:py-4">
                    <LeadSkeleton :width="sk.company" :height="10" />
                    <LeadSkeleton :width="sk.title" :height="14" />
                    <div class="tw:mt-auto tw:flex tw:flex-wrap tw:gap-2 tw:pt-2">
                      <LeadSkeleton :width="60" :height="18" class="tw:rounded-full!" />
                      <LeadSkeleton :width="72" :height="18" class="tw:rounded-full!" />
                      <LeadSkeleton :width="90" :height="18" class="tw:rounded-full!" />
                      <LeadSkeleton :width="80" :height="18" class="tw:rounded-full!" />
                      <LeadSkeleton :width="64" :height="18" class="tw:rounded-full!" />
                      <LeadSkeleton :width="100" :height="18" class="tw:rounded-full!" />
                    </div>
                  </div>
                  <!-- metrics panel placeholder -->
                  <div
                    class="tw:hidden tw:shrink-0 tw:border-l tw:border-solid tw:border-border-secondary tw:pl-5 tw:xl:block"
                    style="width: 460px">
                    <div
                      class="tw:grid tw:gap-6 tw:py-4"
                      style="grid-template-columns: 120px 1fr 1fr; align-content: start">
                      <div class="tw:flex tw:flex-col tw:gap-2">
                        <LeadSkeleton :width="70" :height="8" />
                        <LeadSkeleton :width="100" :height="26" />
                      </div>
                      <div class="tw:flex tw:flex-col tw:gap-2">
                        <LeadSkeleton :width="70" :height="8" />
                        <LeadSkeleton :width="90" :height="18" />
                        <LeadSkeleton :width="60" :height="8" />
                      </div>
                      <div class="tw:flex tw:flex-col tw:gap-2">
                        <LeadSkeleton :width="70" :height="8" />
                        <LeadSkeleton :width="90" :height="18" />
                        <LeadSkeleton :width="60" :height="8" />
                      </div>
                    </div>
                  </div>
                  <!-- creative preview placeholder -->
                  <div class="tw:flex tw:shrink-0 tw:items-center tw:self-center">
                    <LeadSkeleton variant="block" :width="90" :height="80" />
                  </div>
                  <!-- three-dot menu placeholder — 28x28 ghost icon-button, same as OrderActionsMenu -->
                  <div class="tw:flex tw:shrink-0 tw:items-center">
                    <LeadSkeleton variant="block" :width="28" :height="28" />
                  </div>
                </div>
                <!-- spinner row -->
                <div class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:py-4 tw:text-13 tw:text-muted">
                  <span role="status" aria-label="Loading" class="tw:inline-block tw:size-6">
                    <span
                      class="tw:block tw:size-full tw:animate-spin tw:rounded-full tw:border-2 tw:border-solid tw:border-current tw:border-r-transparent tw:motion-reduce:animate-none" />
                  </span>
                  {{ $t('orders.list.loading') }}
                </div>
              </div>
            </template>
            <template #collection="{ collection }">
              <div v-if="listLayout === 'card'" class="tw:mb-4 tw:flex tw:flex-col tw:gap-2.5">
                <OrderGridElement
                  v-for="(item, index) in collection"
                  :key="item.id"
                  :order="item as Order"
                  class="animate__animated animate__fadeInUp animate__faster"
                  :style="{ animationDelay: `${index * 0.1}s` }"
                  @delete="confirmDeleteOrder" />
              </div>
              <div v-else class="animate__animated animate__fadeIn animate__faster">
                <LeadTable
                  :columns="orderTableColumns"
                  :rows="collection as Order[]"
                  :loading="remoteListRef?.isLoading ?? false"
                  :loading-label="$t('orders.list.loading')"
                  :sort-key="sortState.key"
                  :sort-dir="sortState.direction"
                  :page="remoteListRef?.page ?? 1"
                  :page-count="remoteListRef?.pageCount ?? 1"
                  :total="remoteListRef?.filterCount ?? 0"
                  :page-size="10"
                  :item-label="paginationItemLabel"
                  class="tw:my-3"
                  @update:sort="onTableSort"
                  @update:page="(p: number) => remoteListRef?.changePage(p)">
                  <template #cell-title="{ row }">
                    <div class="tw:flex tw:items-center tw:gap-3">
                      <LeadAvatar
                        size="sm"
                        fit="contain"
                        :src="companyLogoUrl(row) || undefined"
                        :initials="companyInitials(row)"
                        :alt="`${row.company_name ?? ''} logo`" />
                      <div class="tw:min-w-0">
                        <RouterLink
                          :to="getOrderRoute(row, selfServiceEnabled)"
                          :title="row.title"
                          data-testid="orders-list-title"
                          class="tw:block tw:truncate tw:font-medium tw:text-gray-900! tw:no-underline tw:hover:text-gray-900! tw:hover:underline">
                          {{ row.title }}
                        </RouterLink>
                        <div class="tw:truncate tw:text-13 tw:text-gray-500">
                          <RouterLink
                            v-if="row.company_name"
                            :to="{ name: 'orders.list', params: { company_name: row.company_name } }"
                            class="tw:text-gray-500! tw:no-underline tw:hover:text-gray-700! tw:hover:underline">
                            {{ row.company_name }}
                          </RouterLink>
                          <span v-if="row.provider_job_id" class="tw:tabular-nums">· {{ row.provider_job_id }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template #cell-status="{ row }">
                    <LeadStatusPill :status="row.is_draft ? 'draft' : row.lead_order_status" />
                  </template>
                  <template #cell-budget="{ row }">
                    <span class="tw:tabular-nums tw:text-gray-900">{{ budgetText(row) }}</span>
                  </template>
                  <template #cell-clicks="{ row }">
                    <span class="tw:tabular-nums tw:text-gray-900">
                      {{ formatCountOrDash(row?.stats_clicks_total) }}
                    </span>
                  </template>
                  <template #cell-ctr="{ row }">
                    <span class="tw:tabular-nums tw:text-gray-900">{{ ctrText(row) }}</span>
                  </template>
                  <template #cell-actions="{ row }">
                    <OrderActionsMenu :order="row" @delete="confirmDeleteOrder" />
                  </template>
                  <template #cell-runtime="{ row }">
                    <div class="tw:flex tw:flex-col tw:gap-1.5">
                      <span class="tw:text-13 tw:font-medium tw:tabular-nums tw:text-gray-900">
                        {{ runtimeText(row) }}
                      </span>
                      <span class="tw:block tw:h-1.5 tw:w-24 tw:rounded-full tw:bg-gray-100">
                        <span
                          class="tw:block tw:h-1.5 tw:rounded-full tw:bg-primary"
                          :style="{ width: `${runtimeProgress(row) * 100}%` }" />
                      </span>
                    </div>
                  </template>
                </LeadTable>
              </div>
            </template>
            <template #footer="{ page, changePage, filterCount, pageCount }">
              <!-- table view has its own internal pagination (rendered in LeadTable's footer band).
                   Card view uses this standalone LeadPagination below the cards. -->
              <LeadPagination
                v-if="listLayout === 'card' && !(remoteListRef?.isLoading ?? true)"
                :page="page"
                :page-count="pageCount"
                :total="filterCount"
                :page-size="10"
                :item-label="paginationItemLabel"
                class="tw:mb-4"
                :hide-when-single-page="true"
                @update:page="
                  (p) => {
                    changePage(p);
                    scrollTopCardIntoView();
                  }
                " />
            </template>
          </RemoteListView>

          <LeadModal
            v-model="deleteConfirmationDialog"
            size="sm"
            :title="$t('orders.details.delete_order.title')"
            :subtitle="orderToDelete?.id ? `#${orderToDelete.id}` : undefined"
            :disable-backdrop-close="deleting"
            :disable-esc-close="deleting"
            :disable-close-button="deleting">
            <p class="tw:m-0 tw:text-14 tw:text-gray-700">
              {{ $t('orders.details.delete_order.description') }}
            </p>
            <div
              v-if="deleteError"
              class="tw:mt-3 tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-14 tw:text-red-700">
              {{ deleteError }}
            </div>
            <template #footer="{ close }">
              <LeadButton variant="ghost" :disabled="deleting" @click="close" data-testid="delete-order-cancel-button">
                {{ $t('btn.cancel') }}
              </LeadButton>
              <LeadButton
                variant="danger"
                :icon="IconTrash"
                :loading="deleting"
                data-testid="delete-order-confirm-button"
                @click="deleteOrder(close)">
                {{ $t('btn.delete') }}
              </LeadButton>
            </template>
          </LeadModal>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted, watch } from 'vue';
import { IconZoomExclamation, IconPlus, IconX, IconTrash } from '@tabler/icons-vue';
import OrderGridElement from '@/components/orders/OrderGridElement.vue';
import OrderActionsMenu from '@/components/orders/OrderActionsMenu.vue';
import ActiveOrdersEmptyView from '@/components/orders/ActiveOrdersEmptyView.vue';
import SortButton from '@/components/generic/SortButton.vue';
import LeadTable, { type LeadTableColumn } from '@/components/lead-design/LeadTable.vue';
import LeadStatusPill from '@/components/lead-design/LeadStatusPill.vue';
import LeadAvatar from '@/components/lead-design/LeadAvatar.vue';
import LeadSkeleton from '@/components/lead-design/LeadSkeleton.vue';
import {
  companyLogoUrl,
  companyInitials,
  runtimeProgress,
  runtimeText,
  formatCountOrDash,
  budgetText,
  ctrText,
} from '@/components/orders/orderFormatters';
import RemoteListView from '../../components/RemoteListView.vue';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import { createFilterEQL, countActiveFilters } from '@/filters/listFilters';

import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import HeaderNavbarSearch from '@/components/header/HeaderNavbarSearch.vue';
import CustomerCards from '@/components/customer/CustomerCards.vue';

import type { SortState } from '@/components/generic/SortButton.vue';
import FilterButton from '@/components/orders/FilterButton.vue';

import { useRoute } from 'vue-router';
import translator, { currentLanguage } from '@/locale/translator';
import type { Order } from '@/types/types';
import ToggleButton from '@/components/common/ToggleButton.vue';
import LeadSegmentedControl from '@/components/lead-design/LeadSegmentedControl.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import { RouterLink } from 'vue-router';
import LeadTabs from '@/components/lead-design/LeadTabs.vue';
import LeadPagination from '@/components/lead-design/LeadPagination.vue';
import {
  IconTable,
  IconLayoutList,
  IconPlayerPlay,
  IconList,
  IconFileText,
  IconUser,
  IconLoader2,
} from '@tabler/icons-vue';
import { useUserStore } from '@/stores/user';
import router from '@/router/router';
import { request } from '@/api/client';
import LeadModal from '@/components/lead-design/LeadModal.vue';
import { useToast } from 'vue-toastification';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { searchFilter } from '@/autopilot/listFilters';
import { buildPjasUri } from '@/func/entityLinks';
import { getOrderRoute } from '@/func/orderCreation';
import { createGotoCreateOrderFunction } from '@/autopilot/functions/orders/createOrder';
import { orderWorkflowGuide } from '@/autopilot/functions/orders/orderWorkflow';
import type { AutopilotListFilter } from '@/composables/useAutopilotList';

const defaultFilters: Record<string, any> = {
  workplace_location: '',
  language: [],
  created_after: '',
  creator_string: '',
  filter_order_lists: 'all_active',
  lead_order_status: '',
};

const options = [
  { key: 'id', label: 'ID' },
  { key: 'created_at', label: translator.get('sort.field.created_at') },
  { key: 'title', label: translator.get('orders.list.sort.title') },
  { key: 'lead_order_status', label: translator.get('order.status') },
  { key: 'stats_clicks_total', label: 'Clicks' },
];

const route = useRoute();
const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();
const selfServiceEnabled = computed(() => featureStore.featureOrderManagment?.self_service === true);
const toast = useToast();
const searchQuery = ref('');
const remoteListRef = ref<InstanceType<typeof RemoteListView>>();
const deleteConfirmationDialog = ref(false);
const orderToDelete = ref<Order | null>(null);
const deleting = ref(false);
const deleteError = ref<string | null>(null);
const totalOrderCount = ref(0);
const filteredOrderCount = ref<number | null>(null);

const sortState = ref<SortState>(getSortSession());

const listLayout = ref<'table' | 'card'>('table');

// columns for the LEAD triage-style orders table. sortKey values must be the same tokens the
// backend accepts for its `order` param. Budget is gated behind the same
// `display_budget` customer feature that hides the budget pill in card view.
const orderTableColumns = computed<LeadTableColumn[]>(() => {
  const cols: LeadTableColumn[] = [
    { key: 'title', label: translator.get('orders.list.column.order') as string, sortKey: 'title', maxWidth: 440 },
    {
      key: 'status',
      label: translator.get('order.status') as string,
      sortKey: 'lead_order_status',
      noWrap: true,
    },
    { key: 'runtime', label: translator.get('orders.list.column.runtime') as string, noWrap: true },
  ];
  if (featureStore.featureOrderManagment?.display_budget) {
    cols.push({ key: 'budget', label: translator.get('orders.list.column.budget') as string, noWrap: true });
  }
  cols.push(
    { key: 'clicks', label: 'Clicks', sortKey: 'stats_clicks_total', noWrap: true },
    { key: 'ctr', label: 'CTR', noWrap: true },
    { key: 'actions', label: '', shrink: true, sticky: 'right' }
  );
  return cols;
});

// A `null` payload from LeadTable is the third click on the active column —
// restore the list's default sort (created_at desc) rather than leaving it
// unsorted.
function onTableSort(payload: { key: string; direction: 'asc' | 'desc' } | null) {
  sortState.value = payload ?? { key: 'created_at', direction: 'desc' };
}

// Skeleton card widths for the grid-view loading state. Varied so the placeholders read as
// heterogeneous content instead of a striped block; cycled per row.
const GRID_SKELETON_WIDTHS = [
  { company: '30%', title: '70%' },
  { company: '25%', title: '55%' },
  { company: '35%', title: '80%' },
  { company: '20%', title: '45%' },
  { company: '28%', title: '65%' },
];
const gridSkeletonRows = Array.from({ length: 5 }, (_, i) => GRID_SKELETON_WIDTHS[i % GRID_SKELETON_WIDTHS.length]);

// English `nav.orders` is title-case ("Orders"), which reads wrong mid-sentence in the pagination
// count line ("1–10 of 213 orders"). Lowercase only in English; other locales are grammatical as-is.
const paginationItemLabel = computed(() => {
  const raw = translator.get('nav.orders') as string;
  return currentLanguage.startsWith('en') ? raw.toLowerCase() : raw;
});

// URL is the single source of truth for filters. `filters` is derived from
// `route.query` (plus a company_name route param and per-filter defaults),
// which means: browser back/forward, deep-linking, and shared URLs all "just
// work" — no watchers, no memory/URL sync, no fetch loops. User actions push
// to the URL via `updateQuery`; the derived `filters` and `requestParameters`
// recompute; RemoteListView's `parameters` watcher fires the refetch.
const filters = computed<Record<string, any>>(() => {
  const merged: Record<string, any> = { company_name: route.params.company_name ?? '' };
  for (const key of Object.keys(defaultFilters)) {
    merged[key] = route.query[key] !== undefined ? route.query[key] : defaultFilters[key];
  }
  return merged;
});

const data = reactive({
  resetPage: false,
});

// Keys that the FilterButton popover exposes — used both for its badge count
// and as the base set the empty-state check extends. `filter_order_lists` is
// the active tab, not a filter, so it's excluded on purpose.
const POPOVER_FILTER_KEYS = [
  'workplace_location',
  'language',
  'created_after',
  'lead_order_status',
  'creator_string',
] as const;
// Anything `clearAndRefetch` would clear counts as "active" for the empty
// state — that includes the search box and the company_name route filter on
// top of the popover keys.
const EMPTY_STATE_FILTER_KEYS = [...POPOVER_FILTER_KEYS, 'company_name'] as const;

const popoverFilterCount = computed(() => countActiveFilters(filters.value, POPOVER_FILTER_KEYS));
const hasActiveFilters = computed(
  () => !!searchQuery.value || countActiveFilters(filters.value, EMPTY_STATE_FILTER_KEYS) > 0
);
// The active-tab empty view replaces the generic empty card whenever the Active tab is empty —
// filtered or not. The component itself swaps its CTA (Go to All vs Reset filters) based on
// `hasActiveFilters`.
const showActiveEmptyView = computed(() => activeTab.value === 'active');
// True once we've fetched at least one page for the Active tab and it came back empty. Used to
// hide surrounding chrome (e.g. the sort button) that would otherwise float above the empty view.
const isActiveTabEmpty = computed(() => activeTab.value === 'active' && filteredOrderCount.value === 0);
const requestParameters = computed(() => ({
  EQL: createFilterEQL(filters.value),
  resolve: 'company,company.logo_image,stats_cache,creator,publications',
}));

// Patch the URL query with the given key/value pairs. Empty values (undefined,
// null, '', []) are removed from the URL entirely, keeping it minimal. Truthy
// values — including deliberate sentinels like `filter_order_lists=all_active`
// or `all` — are always written, so the URL always reflects the tab.
function updateQuery(patch: Record<string, any>) {
  const next: Record<string, any> = { ...route.query };
  for (const [key, value] of Object.entries(patch)) {
    const isEmpty =
      value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    if (isEmpty) delete next[key];
    else next[key] = value;
  }
  router.push({ query: next });
}

// Gates RemoteListView until the URL tab is reconciled — prevents a
// skeleton→rows→skeleton→rows flash from the parameters watcher re-triggering
// loading right as the first response resolves.
const tabReady = ref(false);

// Keeps the active-tab URL param and session storage in sync. URL wins if set;
// otherwise the stored/default tab is pushed into the URL.
async function syncActiveTabWithUrl() {
  const urlTab = route.query.filter_order_lists as string | undefined;
  if (urlTab) {
    sessionStorage.setItem('ordersTab', urlTab);
    return;
  }
  const restored = sessionStorage.getItem('ordersTab') || defaultFilters.filter_order_lists;
  if (restored && !route.query.lead_order_status) {
    await router.replace({ query: { ...route.query, filter_order_lists: restored } });
    sessionStorage.setItem('ordersTab', restored);
  }
}

// Await the replace so RemoteListView mounts with the final route.query.
syncActiveTabWithUrl().finally(() => {
  tabReady.value = true;
});

/* ------------------------------- ON MOUNTED ------------------------------- */
onMounted(() => {
  try {
    const stored = localStorage.getItem('ordersLayout');
    const parsed = stored ? JSON.parse(stored) : null;
    // migrate the pre-rename values so existing users don't lose their preference
    const migrated = parsed === 'list' ? 'table' : parsed === 'grid' ? 'card' : parsed;
    listLayout.value = migrated === 'table' || migrated === 'card' ? migrated : 'table';
  } catch (error) {
    console.error('Error reading layout from localStorage', error);
  }
});

/* -------------------------------- COMPUTED -------------------------------- */
const pageTitle = computed(() => {
  let pageName = translator.get('orders.list.title') as string;
  if (route.params.company_name) {
    pageName = pageName.replace(/\s+/, ` "${route.params.company_name}" `);
  }
  return pageName;
});
const layoutOptions = computed(() => [
  {
    value: 'table' as const,
    icon: IconTable,
    ariaLabel: translator.get('orders.layout.list') as string,
    testId: 'layout-switch-table',
  },
  {
    value: 'card' as const,
    icon: IconLayoutList,
    ariaLabel: translator.get('orders.layout.grid') as string,
    testId: 'layout-switch-card',
  },
]);
type OrderTab = 'active' | 'all' | 'drafts';
const activeTab = computed<OrderTab>(() => {
  if (filters.value.filter_order_lists === 'drafts') return 'drafts';
  if (filters.value.filter_order_lists === 'all_active') return 'active';
  return 'all';
});
const orderTabs = computed(() => {
  const count = filteredOrderCount.value;
  const countFor = (tab: OrderTab) => (tab === activeTab.value && count != null ? count : undefined);
  return [
    {
      value: 'active' as OrderTab,
      label: translator.get('orders.list.filter_active') as string,
      icon: IconPlayerPlay,
      count: countFor('active'),
    },
    {
      value: 'all' as OrderTab,
      label: translator.get('orders.list.filter_all') as string,
      icon: IconList,
      count: countFor('all'),
    },
    {
      value: 'drafts' as OrderTab,
      label: translator.get('orders.list.filter_drafts') as string,
      icon: IconFileText,
      count: countFor('drafts'),
    },
  ];
});
const isListLoading = computed(() => remoteListRef.value?.isLoading ?? true);
const pageSubtitle = computed(() => {
  if (isListLoading.value) return translator.get('orders.list.loading') as string;
  const count = filteredOrderCount.value;
  if (count == null) return '';
  const key = count === 1 ? 'orders.list.column.order' : 'nav.orders';
  const raw = translator.get(key) as string;
  const label = currentLanguage.startsWith('en') ? raw.toLowerCase() : raw;
  return `${count} ${label}`;
});
const sortOptions = computed(() => {
  const sortingOptions = [...options];
  if (featureStore.featureOrderManagment?.display_budget) {
    sortingOptions.push({ key: 'budget_gross_total', label: translator.get('orders.list.column.budget') });
  }
  if (searchQuery.value) {
    sortingOptions.push({ key: 'idfs', label: translator.get('sort.field.relevance') });
  }

  return sortingOptions;
});

const listBreadcrumbs = computed(() => {
  const crumbs: any[] = [{ label: translator.get('nav.orders'), path: { name: 'orders.list' } }];
  if (route.params.company_name) {
    crumbs.push({
      label: route.params.company_name,
      path: { name: 'orders.list', params: { company_name: route.params.company_name } },
    });
  }
  return crumbs;
});

/* ---------------------------------- WATCH --------------------------------- */
// No filters/query watchers needed — `filters` is a computed off `route.query`,
// so any route change (user click, browser back/forward, deep link) recomputes
// filters → recomputes requestParameters → RemoteListView refetches.
watch(searchQuery, (value) => {
  sortState.value = value ? { key: 'idfs', direction: 'asc' } : { key: 'created_at', direction: 'desc' };
});
watch(
  () => sortState.value,
  (value) => {
    sessionStorage.setItem('ordersSort', JSON.stringify(value));
  },
  { deep: true }
);
// Keep the active tab in sync with session storage. This watcher covers two
// cases the onMounted hook can't:
//  - User navigates to another orders.list variant (e.g. company-scoped list
//    from a card click). Same route name → no remount → onMounted doesn't
//    fire. Vue Router drops the query on a params-only navigation, so we
//    have to restore it here.
//  - User switches tab via the tab bar → mirror the new value to storage.
watch(() => route.query.filter_order_lists, syncActiveTabWithUrl);
watch(
  () => listLayout.value,
  (value) => {
    localStorage.setItem('ordersLayout', JSON.stringify(value));
  }
);

/* --------------------------------- METHODS -------------------------------- */
function getSortSession() {
  if (sessionStorage.getItem('ordersSort')) {
    const sessData = JSON.parse(sessionStorage.getItem('ordersSort') as string);
    const sortOptionKeys = [...options.map((item) => item.key), 'budget_gross_total'];
    const sortOptionDirections = ['asc', 'desc'];
    if (sortOptionKeys.includes(sessData?.key) && sortOptionDirections.includes(sessData?.direction)) return sessData;
  }
  return { key: 'created_at', direction: 'desc' };
}
function setActiveTab(tab: OrderTab) {
  // clear the stale count immediately so the badge doesn't flash the previous tab's number
  filteredOrderCount.value = null;
  // reset to page 1 so switching tabs doesn't leave the user stranded on a page that may not
  // exist in the new tab's result set
  data.resetPage = true;
  // 'all_active' | 'all' | 'drafts'. 'all' and 'drafts' are frontend-only sentinels;
  // createFilterEQL translates them (drop entirely / is_draft=1) before hitting the backend.
  updateQuery({
    filter_order_lists: tab === 'active' ? 'all_active' : tab === 'drafts' ? 'drafts' : 'all',
    lead_order_status: '',
  });
}
// FilterButton emits its draft on apply — persist it to the URL wholesale.
function applyDraft(draft: Record<string, any>) {
  updateQuery(draft);
}
function resetFilters() {
  // Reset clears filters only — the active tab stays put. Preserve the current
  // filter_order_lists value (falling back to the default if somehow unset).
  const currentTab = filters.value.filter_order_lists || defaultFilters.filter_order_lists;
  router.push({ query: { filter_order_lists: currentTab } });
}
function clearAndRefetch() {
  searchQuery.value = '';
  resetFilters();
}
function toggleMyOrders() {
  updateQuery({ creator_string: filters.value.creator_string ? '' : userStore.currentUser?.id || '' });
}
function confirmDeleteOrder(order: Order) {
  orderToDelete.value = order;
  deleteError.value = null;
  deleteConfirmationDialog.value = true;
}
async function deleteOrder(close: () => void) {
  if (!orderToDelete.value?.id || deleting.value) return;
  const id = orderToDelete.value.id;
  deleting.value = true;
  deleteError.value = null;
  // enforce a minimum modal-open time so the loading state reads as a real operation
  const minDelay = new Promise((resolve) => setTimeout(resolve, 400));
  try {
    const [response] = await Promise.all([request('DELETE', `/v1/account/order/${id}`, {}, {}), minDelay]);
    if (!response?.success) {
      deleteError.value = translator.get('orders.details.delete_order.error') as string;
      return;
    }
    toast.success('The order has been deleted successfully', { icon: 'ti ti-check' });
    orderToDelete.value = null;
    close();
    remoteListRef.value?.refresh();
  } catch {
    deleteError.value = translator.get('orders.details.delete_order.error') as string;
  } finally {
    deleting.value = false;
  }
}

/* -------------------------------- AUTOPILOT ------------------------------- */
const routeReady = useAutopilotRouteReady();
useAutopilotContext('order_workflow', () => orderWorkflowGuide);
useAutopilotFunctions([createGotoCreateOrderFunction]);

const orderListDescription =
  'The orders list currently on screen. Each row links to the order detail page. Drive it with order_list_set_filters / order_list_paginate; read rows from context.order_list.items.';

function orderListTransformer(order: Order) {
  return {
    id: order.id,
    title: order.title,
    company_name: order.company_name,
    status: order.is_draft ? 'draft' : order.lead_order_status,
    language: order.language,
    workplace_location: order.workplace_location,
    startdate: order.startdate,
    enddate: order.enddate,
  };
}

function orderListLink(order: Order) {
  return buildPjasUri('order', order.id ?? '');
}

const orderListFilters = (): Record<string, AutopilotListFilter> => ({
  search: searchFilter(searchQuery),
  filter_order_lists: {
    schema: {
      type: 'string',
      enum: ['', 'all', 'all_active', 'drafts', 'in_past'],
      description:
        'Quick filter: empty or "all" for all orders, "all_active" for active orders, "drafts" for drafts, "in_past" for past orders.',
    },
    get: () => filters.value.filter_order_lists,
    set: (value) => updateQuery({ filter_order_lists: value ?? '' }),
  },
  my_orders_only: {
    schema: {
      type: 'boolean',
      description: "If true, show only the current user's orders.",
    },
    get: () => !!filters.value.creator_string,
    set: (value) => updateQuery({ creator_string: value ? userStore.currentUser?.id || '' : '' }),
  },
  workplace_location: {
    schema: { type: 'string', description: 'Filter by workplace location text.' },
    get: () => filters.value.workplace_location,
    set: (value) => updateQuery({ workplace_location: value ?? '' }),
  },
  language: {
    schema: {
      type: 'array',
      items: { type: 'string' },
      description: 'Filter by language codes (e.g. ["de", "fr"]).',
    },
    get: () => filters.value.language,
    set: (value) => updateQuery({ language: value ?? [] }),
  },
  created_after: {
    schema: {
      type: 'string',
      description: 'Filter orders created after this ISO date (e.g. "2025-01-01").',
    },
    get: () => filters.value.created_after,
    set: (value) => updateQuery({ created_after: value ?? '' }),
  },
  lead_order_status: {
    schema: {
      type: 'string',
      enum: ['', 'draft', 'to_be_confirmed', 'confirmed', 'running', 'finished'],
      description: 'Filter by order status.',
    },
    get: () => filters.value.lead_order_status,
    set: (value) => updateQuery({ lead_order_status: value ?? '' }),
  },
});

function onAfterFetch() {
  routeReady();
}

function scrollTopCardIntoView() {
  const listEl = remoteListRef.value?.$el as HTMLElement | undefined;
  if (!listEl) return;
  const header = document.querySelector('header');
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const targetY = listEl.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
  window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
}

function onAfterFetchMeta({ totalCount, filterCount }: { totalCount: number; filterCount: number }) {
  totalOrderCount.value = totalCount;
  filteredOrderCount.value = filterCount;
}
</script>
